import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';

import { QaApiService } from '../qa.service';
import {
  TEST_CASE_STATUS_OPTIONS,
  TEST_RESULT_STATUS_OPTIONS,
  TEST_RUN_STATUS_OPTIONS,
  TestCaseDto,
  TestPlanDto,
  TestRunDto
} from '../qa.models';

@Component({
  selector: 'app-qa-detail',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatTableModule, MatProgressSpinnerModule, MatCardModule
  ],
  templateUrl: './qa-detail.component.html',
  styleUrl: './qa-detail.component.scss'
})
export class QaDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly qa = inject(QaApiService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly plan = signal<TestPlanDto | null>(null);
  readonly cases = signal<TestCaseDto[]>([]);
  readonly runs = signal<TestRunDto[]>([]);

  caseTitle = '';
  caseDescription = '';
  caseSteps = '';
  caseExpected = '';
  runName = '';
  selectedRunId = '';
  resultCaseId = '';
  resultStatus = 2;
  resultNotes = '';

  readonly caseStatuses = TEST_CASE_STATUS_OPTIONS;
  readonly runStatuses = TEST_RUN_STATUS_OPTIONS;
  readonly resultStatuses = TEST_RESULT_STATUS_OPTIONS;
  readonly caseColumns = ['title', 'status', 'actions'];
  readonly runColumns = ['name', 'status', 'startedAt', 'actions'];

  get planId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAll(): void {
    this.loading.set(true);
    this.error.set(null);
    this.qa.getById(this.planId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (p) => {
        this.plan.set(p);
        this.runs.set(Array.isArray(p.testRuns) ? p.testRuns : []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load test plan.');
        this.loading.set(false);
      }
    });
    this.qa.listCases(this.planId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.cases.set(Array.isArray(items) ? items : []),
      error: () => this.cases.set([])
    });
  }

  goBack(): void {
    this.router.navigate(['/qa']);
  }

  createCase(): void {
    if (!this.caseTitle.trim()) return;
    this.saving.set(true);
    this.qa.createCase(this.planId, {
      title: this.caseTitle.trim(),
      description: this.caseDescription.trim() || undefined,
      steps: this.caseSteps.trim() || undefined,
      expectedResult: this.caseExpected.trim() || undefined,
      status: 1
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.caseTitle = '';
        this.caseDescription = '';
        this.caseSteps = '';
        this.caseExpected = '';
        this.saving.set(false);
        this.loadAll();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create case.');
        this.saving.set(false);
      }
    });
  }

  deleteCase(c: TestCaseDto): void {
    if (!confirm(`Delete case "${c.title}"?`)) return;
    this.qa.deleteCase(this.planId, c.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadAll(),
      error: (err) => this.error.set(err?.message || 'Failed to delete case.')
    });
  }

  createRun(): void {
    if (!this.runName.trim()) return;
    this.saving.set(true);
    this.qa.createRun(this.planId, { name: this.runName.trim() }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (run) => {
        this.runName = '';
        this.selectedRunId = run.id;
        this.saving.set(false);
        this.loadAll();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create run.');
        this.saving.set(false);
      }
    });
  }

  recordResult(): void {
    if (!this.selectedRunId || !this.resultCaseId) {
      this.error.set('Select a run and test case.');
      return;
    }
    this.saving.set(true);
    this.qa.addResult(this.planId, this.selectedRunId, {
      testCaseId: this.resultCaseId,
      status: this.resultStatus,
      notes: this.resultNotes.trim() || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.resultNotes = '';
        this.saving.set(false);
        this.loadAll();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to record result.');
        this.saving.set(false);
      }
    });
  }

  completeRun(run: TestRunDto): void {
    this.qa.completeRun(this.planId, run.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadAll(),
      error: () => this.loadAll()
    });
  }

  caseStatusLabel(v: number): string {
    return this.caseStatuses.find(s => s.value === v)?.label ?? String(v);
  }

  runStatusLabel(v: number): string {
    return this.runStatuses.find(s => s.value === v)?.label ?? String(v);
  }

  summaryStats(): { total: number; passed: number; failed: number; blocked: number } {
    const runs = this.runs();
    return {
      total: runs.length,
      passed: runs.filter(r => r.status === 2).length,
      failed: runs.filter(r => r.status === 3).length,
      blocked: runs.filter(r => r.status === 4).length
    };
  }
}
